/**
 * Fourteen spans, thrown in the air.
 *
 * It is mounted for exactly as long as a balance is settling and then taken
 * away again, so there is nothing on the page the rest of the time. Where each
 * piece goes is `src/lib/confetti.ts`, which is arithmetic and is tested;
 * what colour it is, is `src/index.css`, which is the only end of this that is
 * allowed to know. The whole flight is one CSS animation per piece on the
 * compositor — no `requestAnimationFrame`, no canvas, nothing on the main
 * thread while it is in the air.
 *
 * `aria-hidden`, because it is not information: the row it is over already
 * says "settled up" in words, and confetti announced to a screen reader is
 * noise in the literal sense.
 */

import type { CSSProperties } from 'react'
import { confettiBurst } from '../lib/confetti'
import { useReducedMotion } from '../motion-react'

/** The burst is deterministic, so it is computed once for the whole app. */
const PIECES = confettiBurst()

export function Confetti() {
  // The one animation reduced motion does not merely flatten but removes: a
  // burst with no movement in it is not a quieter burst, it is a flicker.
  if (useReducedMotion()) return null

  return (
    <span aria-hidden="true" className="confetti">
      {PIECES.map((piece, index) => (
        <span
          key={index}
          className="confetti__piece"
          style={
            {
              '--burst-x': `${piece.x}px`,
              '--burst-peak': `${piece.peak}px`,
              '--burst-y': `${piece.y}px`,
              '--burst-turn': `${piece.turn}deg`,
              '--burst-delay': `${piece.delayMs}ms`,
            } as CSSProperties
          }
        />
      ))}
    </span>
  )
}
